import React from 'react';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { Transaction } from 'components/TransactionAllocator';
import TransactionAllocator from 'components/TransactionAllocator';
import CurrencyRate from 'components/CurrencyRate';
import { CalendarDay } from 'further-ui/utils';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import Spacing from 'components/Spacing';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import api from 'lib/trpcClient';
import { HoldingClassRealisations } from '../types';

type ReviewAccountTransactionsProps = {
  selectedVehicle: Vehicle;
  currency: CurrencyCode;
  currencyForTcyDisplay: CurrencyCode;
  ledgerDate: CalendarDay;
  fxRate: CurrencyRateDetails | null;
  changeRate: (rateDetails: CurrencyRateDetails) => void;
  isFetchingRate: boolean;
  transactionTypeOptions: Array<{
    label: string;
    value: string | null;
  }>;
  isThreeCyFxRateRealisation: boolean;
  threeCyFxRate: CurrencyRateDetails | null;
  threeCyChangeRate: (rateDetails: CurrencyRateDetails) => void;
  threeCyIsFetchingRate: boolean;
  onChangeTransaction: (transactions: Array<Transaction>) => void;
  assetId: string;
  unrealisedAssetFXGainsAndLossesUnwinds: Array<{
    investmentId: string;
    holdingClassId: string;
    unrealisedAssetFXGainAmountToUnwindLcy: number;
    transactions: Array<
      Transaction & {
        isUnrealisedAssetFxGainOrLossUnwind: boolean;
        investmentId: string;
        holdingClassId: string;
      }
    >;
  }>;
  holdingClassRealisation: HoldingClassRealisations;
  holdingClassCurrency: CurrencyCode;
};

const ReviewAccountTransactions: React.FC<ReviewAccountTransactionsProps> = ({
  selectedVehicle,
  currency,
  currencyForTcyDisplay,
  ledgerDate,
  fxRate,
  changeRate,
  isFetchingRate,
  transactionTypeOptions,
  onChangeTransaction,
  assetId,
  unrealisedAssetFXGainsAndLossesUnwinds,
  holdingClassRealisation,
  threeCyIsFetchingRate,
  threeCyFxRate,
  threeCyChangeRate,
  isThreeCyFxRateRealisation,
  holdingClassCurrency,
}) => {
  const forceShowTcy =
    currencyForTcyDisplay !== currency &&
    currencyForTcyDisplay !== selectedVehicle.baseCurrency;

  const buildTransactions = api.lpRealisation.buildTransactions.useMutation();

  const buildTransactionsParams = {
    vehicleId: selectedVehicle._id,
    assetId,
    realisationDate: ledgerDate,
    holdingClassRealisation,
    realisationCurrencyRate: fxRate,
    threeCyRealisationCurrencyRate: threeCyFxRate,
    realisationCurrencyCode: currency,
  };

  const { data: builtTransactions = [] } = useQuery({
    queryKey: ['realisationBuildTransactions', buildTransactionsParams],
    queryFn: async () =>
      await buildTransactions.mutateAsync(buildTransactionsParams),
    placeholderData: keepPreviousData,
    gcTime: 0,
  });

  const finalTransactions = [
    ...builtTransactions,
    ...unrealisedAssetFXGainsAndLossesUnwinds.flatMap(
      (unwind) => unwind.transactions,
    ),
  ].filter(
    (t) =>
      t.amountExVatTcy !== 0 ||
      (t.amountExVatLcy !== 0 && t.amountExVatLcy !== undefined),
  );

  return (
    <Spacing>
      {isThreeCyFxRateRealisation && (
        <CurrencyRate
          currency={holdingClassCurrency}
          date={ledgerDate}
          vehicle={selectedVehicle}
          fxRate={threeCyFxRate}
          onRateChange={threeCyChangeRate}
          isFetchingRate={threeCyIsFetchingRate}
        />
      )}
      <CurrencyRate
        currency={currencyForTcyDisplay}
        date={ledgerDate}
        vehicle={selectedVehicle}
        fxRate={fxRate}
        onRateChange={changeRate}
        isFetchingRate={isFetchingRate}
        forceShow={forceShowTcy}
      />
      <TransactionAllocator
        vehicle={selectedVehicle}
        title="Account transactions (mega-set)"
        transactions={finalTransactions}
        transactionTypeOptions={transactionTypeOptions}
        onChange={onChangeTransaction}
        transactionCurrency={currencyForTcyDisplay}
        fxRate={fxRate}
        forceShowTcy={forceShowTcy}
      />
    </Spacing>
  );
};

export default ReviewAccountTransactions;
